import { ArrowDown01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Select as SelectPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { controlBase } from './input.js';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  /** Accessible name when not wrapped in a FormField. */
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/** Single-select dropdown (Radix Select). Option values must be non-empty strings. */
export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { value, onValueChange, options, placeholder = 'Seleccionar…', disabled, className, id, name, ...aria },
  ref,
) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled} name={name}>
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        className={cn(controlBase, 'flex items-center justify-between gap-2 text-left data-[placeholder]:text-text-secondary', className)}
        {...aria}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <Icon icon={ArrowDown01Icon} aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="z-50 max-h-[min(320px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-menu border border-border-subtle bg-surface-default shadow-overlay animate-fade-in"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex cursor-pointer select-none items-center gap-2 rounded-control py-2 pl-8 pr-3 text-body text-text-primary outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-surface-subtle data-[state=checked]:font-medium data-[disabled]:opacity-50"
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex">
                  <Icon icon={Tick02Icon} aria-hidden className="size-4 text-brand-primary" />
                </SelectPrimitive.ItemIndicator>
                <div className="grid">
                  <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                  {o.description ? <span className="text-meta text-text-secondary">{o.description}</span> : null}
                </div>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
